// src/store/notifications.js
import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import notificationInboxService from '@/services/notificationInboxService';

export const useNotificationsStore = defineStore('notifications', () => {
  // State
  const items = ref([]);
  const unreadCount = ref(0);
  const isLoading = ref(false);
  const error = ref(null);
  // Backend may not be deployed yet; when the endpoint 404s we keep the
  // inbox in a graceful empty state instead of erroring.
  const isAvailable = ref(true);

  // Getters
  const hasUnread = computed(() => unreadCount.value > 0);

  // Actions
  async function fetchNotifications(params = {}) {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await notificationInboxService.getAll(params);
      items.value = response.items || [];
      unreadCount.value = response.unread || 0;
      isAvailable.value = true;
      return response;
    } catch (err) {
      if (err.response && err.response.status === 404) {
        isAvailable.value = false;
        items.value = [];
        unreadCount.value = 0;
      } else {
        error.value = err.message || 'Failed to fetch notifications';
      }
      return null;
    } finally {
      isLoading.value = false;
    }
  }

  async function markRead(id) {
    const target = items.value.find((n) => n._id === id);
    if (!target || target.read) return;

    target.read = true;
    unreadCount.value = Math.max(0, unreadCount.value - 1);

    try {
      await notificationInboxService.markRead(id);
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  }

  async function markAllRead() {
    items.value.forEach((n) => {
      n.read = true;
    });
    unreadCount.value = 0;

    try {
      await notificationInboxService.markAllRead();
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
    }
  }

  return {
    // State
    items,
    unreadCount,
    isLoading,
    error,
    isAvailable,

    // Getters
    hasUnread,

    // Actions
    fetchNotifications,
    markRead,
    markAllRead,
  };
});
